













































































































































































































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import { ascending, max, min, range } from 'd3-array';
import { ContentLoader } from 'vue-content-loader';
import BarMetricCard from '@/components/UI/BarMetricCard.vue';
import CardLoader from '@/components/UI/Loader/CardLoader.vue';
import IconCard from '@/components/UI/IconCard.vue';
import {
  ICampaignBlueprint,
  IAudience,
  LoadState,
  Offer,
  ICommonABTest,
  CampaignOffer,
  ICampaignDiscountOffer,
  IEmailStatusCount,
} from '@/store/types';
import { capitalizeFirstLetter, LOAD_STATE, OFFER_TYPE, romanize } from '@/store/shared';
import { FETCH_REACTIVATION_CAMPAIGN_BLUEPRINT } from '@/store/action-types';

enum CampaignBlueprintState {
  disabled = 'disabled',
  setup = 'setup',
  collecting = 'collecting',
  active = 'active',
}

export default Vue.extend({
  name: 'CampaignBlueprintCard',
  components: {
    BarMetricCard,
    CardLoader,
    ContentLoader,
    IconCard,
  },
  props: {
    campaignBlueprintId: {
      type: String,
    },
  },
  data() {
    return {
      LOAD_STATE,
      CampaignBlueprintState,
    };
  },
  async created() {
    if (this.campaignBlueprintId && !this.$store.state.reactivationCampaignBlueprints?.[this.campaignBlueprintId]) {
      await this.$store.dispatch(FETCH_REACTIVATION_CAMPAIGN_BLUEPRINT, this.campaignBlueprintId);
    }
  },
  computed: {
    ...mapGetters({ abTestsEnabled: 'reactivationABTestsEnabled' }),
    campaignEmailOpenCounts(): IEmailStatusCount | undefined {
      return this.$store.state.reactivationEmailOpenCounts[this.campaignBlueprintId];
    },
    sent(): number {
      if (this.campaignEmailOpenCounts) {
        return this.campaignEmailOpenCounts.sent || 0;
      }
      return 0;
    },
    opens(): number {
      if (this.campaignEmailOpenCounts) {
        return this.campaignEmailOpenCounts.opened || 0;
      }
      return 0;
    },
    clicks(): number {
      if (this.campaignEmailOpenCounts) {
        return this.campaignEmailOpenCounts.clicked || 0;
      }
      return 0;
    },
    reactivations(): number {
      if (this.campaignEmailOpenCounts) {
        return this.campaignEmailOpenCounts.recovered || 0;
      }
      return 0;
    },
    openRate(): number {
      if (this.opens && this.sent) {
        return Math.round((this.opens / this.sent) * 100);
      }
      return 0;
    },
    clickRate(): number {
      if (this.clicks && this.opens) {
        return Math.round((this.clicks / this.opens) * 100);
      }
      return 0;
    },
    reactivationRate(): number {
      if (this.reactivations && this.clicks) {
        return Math.round((this.reactivations / this.clicks) * 100);
        // return parseFloat(((this.reactivations / this.clicks) * 100).toFixed(1));
      }
      return 0;
    },
    activeCampaignCount(): number | undefined {
      if (this.$store.state.reactivationCampaignCountsLoadState !== LOAD_STATE.LOADED) {
        return undefined;
      }
      return this.$store.state.reactivationCampaignCounts[this.campaignBlueprintId] || 0;
    },
    emailsSentLast30Days(): number {
      return this.$store.state.campaignBlueprintEmailCounts[this.campaignBlueprintId]?.count;
    },
    campaignBlueprint(): ICampaignBlueprint | undefined {
      if (!this.campaignBlueprintId) {
        return undefined;
      }
      return this.$store.state.reactivationCampaignBlueprints[this.campaignBlueprintId];
    },
    unpublishedChanges(): boolean {
      if (this.campaignBlueprint) {
        return this.campaignBlueprint.updatedAt !== this.campaignBlueprint.publishedAt;
      }
      return false;
    },
    primaryNeedsPublishing(): boolean {
      if (!this.audience && !this.$store.state.org?.reactivation?.activeCampaign) {
        return true;
      }
      return false;
    },
    audience(): IAudience | undefined {
      return this.campaignBlueprint?.audience;
    },
    hasABTest(): boolean {
      if (!this.abTestsEnabled) return false;
      return !!this.campaignBlueprint?.abTests?.some((abTest: ICommonABTest) => !abTest.completedAt);
    },
    campaignBlueprintState(): CampaignBlueprintState {
      if (!this.campaignBlueprint?.enabled) {
        return CampaignBlueprintState.disabled;
      }
      if (!this.campaignBlueprint?.publishedAt) {
        return CampaignBlueprintState.setup;
      }
      return CampaignBlueprintState.active;
    },
    campaignBlueprintLoadState(): LoadState {
      if (this.campaignBlueprintId) {
        return this.campaignBlueprint?.loadState || LOAD_STATE.UNLOADED;
      }
      return LOAD_STATE.LOADED;
    },
    loadState(): LoadState {
      if (this.campaignBlueprintLoadState === LOAD_STATE.LOADED) {
        return LOAD_STATE.LOADED;
      }
      return LOAD_STATE.LOADING;
    },
    audienceDescription(): string {
      if (this.audience) {
        const filters = this.audience.filters || [];
        const filterStrings = filters.map((f) => this.$store.getters.segmentFilterToString(f));
        if (filterStrings.length) {
          if (filterStrings.length > 1) {
            return filterStrings.map((s, i) => `(${romanize(i + 1).toLowerCase()}) ${capitalizeFirstLetter(s.toLowerCase())}`).join(', and ');
          }
          return capitalizeFirstLetter(filterStrings.join(' & ').toLowerCase());
        }
      } else {
        return 'Customers who do not belong to any other segmented campaigns';
      }
      return 'To be defined';
    },
    offerDescription(): string {
      const offers = (this.campaignBlueprint?.emails || [])
        .map((e) => e.offers)
        .flat()
        // @ts-ignore
        .filter((d) => (d.offerType = OFFER_TYPE.DISCOUNT)) as ICampaignDiscountOffer[];
      const couponsOffered = [...new Set(offers.map((e) => e.discountConfig.couponId))];
      const couponCount = couponsOffered.length;
      if (!couponCount) {
        return 'No offers';
      }
      return couponsOffered.map((c) => this.$store.getters.couponIdToString(c)).join(', ');
    },
    timingDescription(): string {
      if (this.campaignBlueprint?.emails?.length) {
        const minDay = min(this.campaignBlueprint?.emails.map((e) => e.sendOnDay));
        const maxDay = max(this.campaignBlueprint?.emails.map((e) => e.sendOnDay));
        if (minDay === maxDay) {
          return `${minDay} ${minDay === 1 ? 'day' : 'days'} after subscription cancellation`;
        }
        return `${minDay} to ${maxDay} ${maxDay === 1 ? 'day' : 'days'} after subscription cancellation`;
      }
      return 'After subscription cancellation';
    },
  },
});
